import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { adminAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import UserCredentialsModal from '../components/UserCredentialsModal';
import { toast } from 'sonner';
import { 
  Users, BookOpen, LogOut, Loader2, Home, 
  Search, ChevronRight, AlertCircle, CheckCircle, XCircle,
  Mail, Phone, Clock, Shield, UserCheck, UserX, GraduationCap, Edit, Settings,
  Download, Upload, FileSpreadsheet, Plus, Trash2, Video, Image, Globe
} from 'lucide-react';

const AdminDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [dashboard, setDashboard] = useState(null);
  const [pendingTeachers, setPendingTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  
  // Approval/Rejection modal
  const [actionModal, setActionModal] = useState({ open: false, teacher: null, action: null });
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // User credentials modal
  const [credentialsModal, setCredentialsModal] = useState({ open: false, user: null });
  
  // CSV import state
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(true);
  
  // Create teacher modal state
  const [createTeacherModal, setCreateTeacherModal] = useState(false);
  const [newTeacher, setNewTeacher] = useState({
    name: '', email: '', password: '', phone: '', bio: '', bio_ar: '',
    profile_image: '', intro_video_url: '', specializations: '', auto_approve: true
  });
  const [creatingTeacher, setCreatingTeacher] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Edit teacher modal state
  const [editTeacherModal, setEditTeacherModal] = useState({ open: false, teacher: null });
  const [editTeacher, setEditTeacher] = useState({
    name: '', email: '', phone: '', bio: '', bio_ar: '',
    profile_image: '', intro_video_url: '', specializations: ''
  });
  const [updatingTeacher, setUpdatingTeacher] = useState(false);
  
  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ open: false, teacher: null });
  const [deleting, setDeleting] = useState(false);

  // Marquee teachers state
  const [marqueeTeachers, setMarqueeTeachers] = useState([]);
  const [marqueeModal, setMarqueeModal] = useState({ open: false, teacher: null });
  const [newMarqueeTeacher, setNewMarqueeTeacher] = useState({
    name: '', title_en: '', title_ar: '', image: null
  });
  const [savingMarquee, setSavingMarquee] = useState(false);
  const marqueeFileInputRef = useRef(null);

  const content = {
    en: {
      title: "Admin Portal",
      welcome: "Welcome",
      pending: "Pending Approval",
      allTeachers: "All Teachers",
      manageUsers: "Manage Users",
      students: "Students",
      overview: "Overview",
      totalUsers: "Total Users",
      totalStudents: "Total Students",
      totalTeachers: "Total Teachers",
      pendingTeachers: "Pending Teachers",
      searchPlaceholder: "Search by name or email...",
      approve: "Approve",
      reject: "Reject",
      revoke: "Revoke",
      edit: "Edit",
      filterByRole: "Filter by role",
      all: "All",
      moderators: "Moderators",
      teachers: "Teachers",
      noTeachers: "No teachers found",
      noStudents: "No students found",
      viewDetails: "View Details",
      status: "Status",
      approved: "Approved",
      pendingStatus: "Pending",
      rejected: "Rejected",
      logout: "Logout",
      backToHome: "Back to Home",
      confirmApprove: "Approve Teacher",
      // CSV Export/Import
      exportCSV: "Export CSV",
      importCSV: "Import CSV",
      downloadTemplate: "Download Template",
      importSuccess: "Import completed successfully!",
      imported: "imported",
      skipped: "skipped",
      emailsSent: "emails sent",
      sendWelcomeEmails: "Send welcome emails with credentials",
      selectFile: "Select CSV File",
      confirmReject: "Reject Teacher",
      approveMessage: "Are you sure you want to approve this teacher? They will be displayed on the public website.",
      rejectMessage: "Please provide a reason for rejection (optional):",
      rejectReason: "Rejection Reason",
      cancel: "Cancel",
      confirm: "Confirm",
      approvalSuccess: "Teacher approved successfully!",
      rejectionSuccess: "Teacher rejected successfully!",
      specializations: "Specializations",
      bio: "Bio",
      createdAt: "Created",
      createdBy: "Added by",
      noUsers: "No users found",
      // Teacher management
      addTeacher: "Add Teacher",
      createTeacher: "Create Teacher",
      editTeacher: "Edit Teacher",
      updateTeacher: "Update Teacher",
      deleteTeacher: "Delete Teacher",
      deleteConfirmTitle: "Delete Teacher",
      deleteConfirmMessage: "Are you sure you want to permanently delete this teacher? This action cannot be undone.",
      teacherCreated: "Teacher created successfully!",
      teacherUpdated: "Teacher updated successfully!",
      teacherDeleted: "Teacher deleted successfully!",
      teacherName: "Name",
      teacherEmail: "Email",
      teacherPassword: "Password",
      teacherPhone: "Phone",
      teacherBio: "Bio (English)",
      teacherBioAr: "Bio (Arabic)",
      profileImage: "Profile Image",
      uploadImage: "Upload Image",
      changeImage: "Change Image",
      introVideo: "Intro Video URL",
      autoApprove: "Auto-approve (display on website immediately)"
    },
    ar: {
      title: "بوابة المدير",
      welcome: "مرحباً",
      pending: "في انتظار الموافقة",
      allTeachers: "جميع المعلمين",
      manageUsers: "إدارة المستخدمين",
      students: "الطلاب",
      overview: "نظرة عامة",
      totalUsers: "إجمالي المستخدمين",
      totalStudents: "إجمالي الطلاب",
      totalTeachers: "إجمالي المعلمين",
      pendingTeachers: "المعلمين في الانتظار",
      searchPlaceholder: "ابحث بالاسم أو البريد الإلكتروني...",
      approve: "موافقة",
      reject: "رفض",
      revoke: "إلغاء",
      edit: "تعديل",
      filterByRole: "تصفية حسب الدور",
      all: "الكل",
      moderators: "المشرفين",
      teachers: "المعلمين",
      noTeachers: "لم يتم العثور على معلمين",
      noStudents: "لم يتم العثور على طلاب",
      viewDetails: "عرض التفاصيل",
      status: "الحالة",
      approved: "موافق عليه",
      pendingStatus: "في الانتظار",
      rejected: "مرفوض",
      logout: "تسجيل الخروج",
      backToHome: "العودة للرئيسية",
      confirmApprove: "الموافقة على المعلم",
      confirmReject: "رفض المعلم",
      approveMessage: "هل أنت متأكد من الموافقة على هذا المعلم؟ سيتم عرضه على الموقع العام.",
      rejectMessage: "يرجى تقديم سبب الرفض (اختياري):",
      rejectReason: "سبب الرفض",
      cancel: "إلغاء",
      confirm: "تأكيد",
      approvalSuccess: "تمت الموافقة على المعلم بنجاح!",
      rejectionSuccess: "تم رفض المعلم بنجاح!",
      specializations: "التخصصات",
      bio: "السيرة الذاتية",
      createdAt: "تاريخ الإنشاء",
      createdBy: "أضيف بواسطة",
      // CSV Export/Import
      exportCSV: "تصدير CSV",
      importCSV: "استيراد CSV",
      downloadTemplate: "تحميل القالب",
      importSuccess: "تم الاستيراد بنجاح!",
      imported: "تم استيراده",
      skipped: "تم تخطيه",
      emailsSent: "رسائل مرسلة",
      sendWelcomeEmails: "إرسال رسائل ترحيبية مع بيانات الدخول",
      selectFile: "اختر ملف CSV",
      noUsers: "لم يتم العثور على مستخدمين",
      // Teacher management
      addTeacher: "إضافة معلم",
      createTeacher: "إنشاء معلم",
      editTeacher: "تعديل المعلم",
      updateTeacher: "تحديث المعلم",
      deleteTeacher: "حذف المعلم",
      deleteConfirmTitle: "حذف المعلم",
      deleteConfirmMessage: "هل أنت متأكد من حذف هذا المعلم نهائياً؟ لا يمكن التراجع عن هذا الإجراء.",
      teacherCreated: "تم إنشاء المعلم بنجاح!",
      teacherUpdated: "تم تحديث المعلم بنجاح!",
      teacherDeleted: "تم حذف المعلم بنجاح!",
      teacherName: "الاسم",
      teacherEmail: "البريد الإلكتروني",
      teacherPassword: "كلمة المرور",
      teacherPhone: "الهاتف",
      teacherBio: "السيرة (إنجليزي)",
      teacherBioAr: "السيرة (عربي)",
      profileImage: "صورة الملف الشخصي",
      uploadImage: "رفع صورة",
      changeImage: "تغيير الصورة",
      introVideo: "رابط فيديو التعريف",
      autoApprove: "موافقة تلقائية (عرض على الموقع فوراً)"
    }
  };

  const t = content[language];

  const fetchAllUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers(userRoleFilter || null);
      setAllUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await adminAPI.getAllUsers('student');
      setAllStudents(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  };

  const fetchMarqueeTeachers = async () => {
    try {
      const response = await adminAPI.getMarqueeTeachers();
      setMarqueeTeachers(response.data.marquee_teachers || []);
    } catch (err) {
      console.error('Failed to fetch marquee teachers:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchAllUsers();
    }
    if (activeTab === 'students') {
      fetchStudents();
    }
    if (activeTab === 'marquee') {
      fetchMarqueeTeachers();
    }
  }, [activeTab, userRoleFilter]);

  const handleSaveCredentials = async (userId, updates) => {
    await adminAPI.updateAdminUserCredentials(userId, updates);
    // Refresh data
    fetchAllUsers();
    fetchStudents();
    const dashRes = await adminAPI.getDashboard();
    setDashboard(dashRes.data);
  };

  // CSV Export
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await adminAPI.exportStudentsCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `students_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تصدير الطلاب بنجاح' : 'Students exported successfully');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تصدير CSV' : 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // Download Template
  const handleDownloadTemplate = async () => {
    try {
      const response = await adminAPI.getImportTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'students_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تحميل القالب' : 'Template downloaded');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحميل القالب' : 'Failed to download template');
    }
  };

  // CSV Import
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await adminAPI.importStudentsCSV(file, sendWelcomeEmails);
      setImportResult(response.data.results);
      // Refresh data
      fetchStudents();
      const dashRes = await adminAPI.getDashboard();
      setDashboard(dashRes.data);
      
      const emailsMsg = sendWelcomeEmails && response.data.results.emails_sent > 0 
        ? `, ${response.data.results.emails_sent} ${language === 'ar' ? 'رسائل مرسلة' : 'emails sent'}`
        : '';
      
      toast.success(
        language === 'ar' 
          ? `تم استيراد ${response.data.results.imported} طالب${emailsMsg}` 
          : `${response.data.results.imported} students imported successfully${emailsMsg}`
      );
    } catch (err) {
      const errorMsg = err.response?.data?.detail || (language === 'ar' ? 'فشل في استيراد CSV' : 'Failed to import CSV');
      toast.error(errorMsg);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (!authLoading && user && user.role !== 'admin') {
      navigate(`/${user.role}`);
      return;
    }

    const fetchData = async () => {
      try {
        const [dashboardRes, pendingRes, allRes] = await Promise.all([
          adminAPI.getDashboard(),
          adminAPI.getPendingTeachers(),
          adminAPI.getAllTeachers()
        ]);
        
        setDashboard(dashboardRes.data);
        setPendingTeachers(pendingRes.data.teachers || []);
        setAllTeachers(allRes.data.teachers || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleApprove = async () => {
    if (!actionModal.teacher) return;
    setSubmitting(true);
    
    try {
      await adminAPI.approveTeacher(actionModal.teacher.id);
      setSuccessMessage(t.approvalSuccess);
      
      // Refresh data
      const [pendingRes, allRes] = await Promise.all([
        adminAPI.getPendingTeachers(),
        adminAPI.getAllTeachers()
      ]);
      setPendingTeachers(pendingRes.data.teachers || []);
      setAllTeachers(allRes.data.teachers || []);
      
      // Update dashboard
      const dashRes = await adminAPI.getDashboard();
      setDashboard(dashRes.data);
      
      setTimeout(() => {
        setActionModal({ open: false, teacher: null, action: null });
        setSuccessMessage('');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve teacher');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!actionModal.teacher) return;
    setSubmitting(true);
    
    try {
      await adminAPI.rejectTeacher(actionModal.teacher.id, rejectReason || null);
      setSuccessMessage(t.rejectionSuccess);
      
      // Refresh data
      const [pendingRes, allRes] = await Promise.all([
        adminAPI.getPendingTeachers(),
        adminAPI.getAllTeachers()
      ]);
      setPendingTeachers(pendingRes.data.teachers || []);
      setAllTeachers(allRes.data.teachers || []);
      
      // Update dashboard
      const dashRes = await adminAPI.getDashboard();
      setDashboard(dashRes.data);
      
      setTimeout(() => {
        setActionModal({ open: false, teacher: null, action: null });
        setRejectReason('');
        setSuccessMessage('');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reject teacher');
    } finally {
      setSubmitting(false);
    }
  };

  // Image upload handler
  const handleImageUpload = async (event, isEdit = false) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'ar' ? 'نوع الملف غير مدعوم' : 'Invalid file type');
      return;
    }
    
    setUploadingImage(true);
    try {
      const response = await adminAPI.uploadTeacherImage(file);
      const imageUrl = response.data.url;
      
      if (isEdit) {
        setEditTeacher(prev => ({ ...prev, profile_image: imageUrl }));
      } else {
        setNewTeacher(prev => ({ ...prev, profile_image: imageUrl }));
      }
      
      toast.success(language === 'ar' ? 'تم رفع الصورة بنجاح' : 'Image uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في رفع الصورة' : 'Failed to upload image'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Create teacher handler
  const handleCreateTeacher = async () => {
    if (!newTeacher.name || !newTeacher.email || !newTeacher.password) {
      toast.error(language === 'ar' ? 'يرجى ملء الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    
    setCreatingTeacher(true);
    try {
      await adminAPI.createTeacherProfile(newTeacher);
      toast.success(t.teacherCreated);
      
      // Reset form and close modal
      setNewTeacher({
        name: '', email: '', password: '', phone: '', bio: '', bio_ar: '',
        profile_image: '', intro_video_url: '', specializations: '', auto_approve: true
      });
      setCreateTeacherModal(false);
      
      // Refresh data
      const [pendingRes, allRes, dashRes] = await Promise.all([
        adminAPI.getPendingTeachers(),
        adminAPI.getAllTeachers(),
        adminAPI.getDashboard()
      ]);
      setPendingTeachers(pendingRes.data.teachers || []);
      setAllTeachers(allRes.data.teachers || []);
      setDashboard(dashRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في إنشاء المعلم' : 'Failed to create teacher'));
    } finally {
      setCreatingTeacher(false);
    }
  };

  // Open edit teacher modal
  const openEditTeacherModal = async (teacher) => {
    try {
      const response = await adminAPI.getTeacherDetails(teacher.id);
      const teacherData = response.data;
      
      setEditTeacher({
        name: teacherData.name || '',
        email: teacherData.email || '',
        phone: teacherData.phone || '',
        bio: teacherData.bio || '',
        bio_ar: teacherData.bio_ar || '',
        profile_image: teacherData.profile_image || '',
        intro_video_url: teacherData.intro_video_url || '',
        specializations: (teacherData.specializations || []).join(', ')
      });
      setEditTeacherModal({ open: true, teacher: teacherData });
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحميل بيانات المعلم' : 'Failed to load teacher data');
    }
  };

  // Update teacher handler
  const handleUpdateTeacher = async () => {
    if (!editTeacherModal.teacher) return;
    
    setUpdatingTeacher(true);
    try {
      await adminAPI.updateTeacherProfile(editTeacherModal.teacher.id, editTeacher);
      toast.success(t.teacherUpdated);
      setEditTeacherModal({ open: false, teacher: null });
      
      // Refresh data
      const [pendingRes, allRes, dashRes] = await Promise.all([
        adminAPI.getPendingTeachers(),
        adminAPI.getAllTeachers(),
        adminAPI.getDashboard()
      ]);
      setPendingTeachers(pendingRes.data.teachers || []);
      setAllTeachers(allRes.data.teachers || []);
      setDashboard(dashRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في تحديث المعلم' : 'Failed to update teacher'));
    } finally {
      setUpdatingTeacher(false);
    }
  };

  // Delete teacher handler
  const handleDeleteTeacher = async () => {
    if (!deleteModal.teacher) return;
    
    setDeleting(true);
    try {
      await adminAPI.deleteTeacherProfile(deleteModal.teacher.id);
      toast.success(t.teacherDeleted);
      setDeleteModal({ open: false, teacher: null });
      
      // Refresh data
      const [pendingRes, allRes, dashRes] = await Promise.all([
        adminAPI.getPendingTeachers(),
        adminAPI.getAllTeachers(),
        adminAPI.getDashboard()
      ]);
      setPendingTeachers(pendingRes.data.teachers || []);
      setAllTeachers(allRes.data.teachers || []);
      setDashboard(dashRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في حذف المعلم' : 'Failed to delete teacher'));
    } finally {
      setDeleting(false);
    }
  };

  // Marquee teachers handlers
  const handleSaveMarqueeTeacher = async () => {
    if (!newMarqueeTeacher.name || !newMarqueeTeacher.title_en || !newMarqueeTeacher.title_ar) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    // Check for image (either file or URL)
    const hasImage = newMarqueeTeacher.image || (newMarqueeTeacher.image_url && newMarqueeTeacher.image_url.trim());
    if (!marqueeModal.teacher && !hasImage) {
      toast.error(language === 'ar' ? 'يرجى اختيار صورة أو إدخال رابط' : 'Please select an image or enter URL');
      return;
    }

    setSavingMarquee(true);
    try {
      if (marqueeModal.teacher) {
        // Update existing
        await adminAPI.updateMarqueeTeacher(marqueeModal.teacher.id, newMarqueeTeacher);
        toast.success(language === 'ar' ? 'تم التحديث بنجاح' : 'Teacher updated');
      } else {
        // Create new
        await adminAPI.createMarqueeTeacher(newMarqueeTeacher);
        toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Teacher added');
      }
      setMarqueeModal({ open: false, teacher: null });
      setNewMarqueeTeacher({ name: '', title_en: '', title_ar: '', image: null, image_url: '' });
      setUseImageUrl(false);
      fetchMarqueeTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'Error occurred'));
    } finally {
      setSavingMarquee(false);
    }
  };

  const handleDeleteMarqueeTeacher = async (teacherId) => {
    if (!confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) return;
    
    try {
      await adminAPI.deleteMarqueeTeacher(teacherId);
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Teacher deleted');
      fetchMarqueeTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحذف' : 'Failed to delete'));
    }
  };

  const displayedTeachers = activeTab === 'pending' ? pendingTeachers : allTeachers;
  const filteredTeachers = displayedTeachers.filter(teacher => 
    teacher.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const styles = {
      moderator: 'bg-purple-100 text-purple-700',
      teacher: 'bg-blue-100 text-blue-700',
      student: 'bg-green-100 text-green-700',
      affiliate: 'bg-amber-100 text-amber-700'
    };
    const labels = {
      en: { moderator: 'Moderator', teacher: 'Teacher', student: 'Student', affiliate: 'Affiliate' },
      ar: { moderator: 'مشرف', teacher: 'معلم', student: 'طالب', affiliate: 'شريك' }
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[role] || 'bg-gray-100 text-gray-700'}`}>
        {labels[language][role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: t.pendingStatus,
      approved: t.approved,
      rejected: t.rejected
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => { setError(null); window.location.reload(); }}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="text-red-100">{t.welcome}, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={toggleLanguage}
              variant="secondary"
              size="sm"
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            <Link to="/" className="text-red-100 hover:text-white flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">{t.backToHome}</span>
            </Link>
            <Button variant="secondary" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <Users className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_users || 0}</h3>
            <p className="text-gray-600">{t.totalUsers}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <BookOpen className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_students || 0}</h3>
            <p className="text-gray-600">{t.totalStudents}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <GraduationCap className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_teachers || 0}</h3>
            <p className="text-gray-600">{t.totalTeachers}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-yellow-200 bg-yellow-50">
            <Clock className="w-8 h-8 text-yellow-600 mb-3" />
            <h3 className="text-3xl font-bold text-yellow-700">{dashboard?.pending_teachers || 0}</h3>
            <p className="text-yellow-600 font-medium">{t.pendingTeachers}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === 'pending'
                    ? 'text-red-600 border-b-2 border-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="pending-tab"
              >
                {t.pending}
                {pendingTeachers.length > 0 && (
                  <span className="ml-2 bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs">
                    {pendingTeachers.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === 'all'
                    ? 'text-red-600 border-b-2 border-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="all-teachers-tab"
              >
                {t.allTeachers}
              </button>
              <button
                onClick={() => { setActiveTab('users'); setSearchQuery(''); }}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === 'users'
                    ? 'text-red-600 border-b-2 border-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="manage-users-tab"
              >
                <Settings className="w-4 h-4 inline mr-1" />
                {t.manageUsers}
              </button>
              <button
                onClick={() => { setActiveTab('students'); setSearchQuery(''); }}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === 'students'
                    ? 'text-red-600 border-b-2 border-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="students-tab"
              >
                <FileSpreadsheet className="w-4 h-4 inline mr-1" />
                {t.students}
              </button>
              <button
                onClick={() => { setActiveTab('marquee'); setSearchQuery(''); }}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === 'marquee'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="marquee-tab"
              >
                <Image className="w-4 h-4 inline mr-1" />
                {language === 'en' ? 'Marquee Photos' : 'صور المعلمين المتحركة'}
              </button>
            </div>
          </div>

          <div className="p-6 border-b">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.searchPlaceholder}
                  className={`${language === 'ar' ? 'pr-10' : 'pl-10'}`}
                />
              </div>
              {activeTab === 'users' && (
                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">{t.all}</option>
                  <option value="moderator">{t.moderators}</option>
                  <option value="teacher">{t.teachers}</option>
                  <option value="student">{t.students}</option>
                </select>
              )}
              {(activeTab === 'pending' || activeTab === 'all') && (
                <Button
                  onClick={() => setCreateTeacherModal(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="add-teacher-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t.addTeacher}
                </Button>
              )}
            </div>
          </div>
          
          {/* Teachers Tab Content */}
          {(activeTab === 'pending' || activeTab === 'all') && (
          <div className="divide-y">
            {filteredTeachers.length > 0 ? (
              filteredTeachers.map((teacher) => (
                <div key={teacher.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{teacher.name}</h3>
                        {getStatusBadge(teacher.teacher_status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {teacher.email}
                        </span>
                        {teacher.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {teacher.phone}
                          </span>
                        )}
                      </div>
                      {teacher.bio && (
                        <p className="text-sm text-gray-500 mb-2">{teacher.bio}</p>
                      )}
                      {teacher.profile?.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {teacher.profile.specializations.map((spec, idx) => (
                            <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                              {spec}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {/* Edit button for all teachers */}
                      <Button 
                        onClick={() => openEditTeacherModal(teacher)}
                        variant="outline"
                        size="sm"
                        data-testid={`edit-teacher-btn-${teacher.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t.edit}
                      </Button>
                      {teacher.teacher_status === 'pending' && (
                        <>
                          <Button 
                            onClick={() => setActionModal({ open: true, teacher, action: 'approve' })}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                            data-testid={`approve-btn-${teacher.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            {t.approve}
                          </Button>
                          <Button 
                            onClick={() => setActionModal({ open: true, teacher, action: 'reject' })}
                            variant="destructive"
                            size="sm"
                            data-testid={`reject-btn-${teacher.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            {t.reject}
                          </Button>
                        </>
                      )}
                      {teacher.teacher_status === 'approved' && (
                        <Button 
                          onClick={() => setActionModal({ open: true, teacher, action: 'reject' })}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          {t.revoke}
                        </Button>
                      )}
                      {/* Delete button for all teachers */}
                      <Button 
                        onClick={() => setDeleteModal({ open: true, teacher })}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-teacher-btn-${teacher.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t.noTeachers}</p>
              </div>
            )}
          </div>
          )}

          {/* Users Tab Content */}
          {activeTab === 'users' && (
            <div className="divide-y">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((u) => (
                  <div key={u.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{u.name}</h3>
                          {getRoleBadge(u.role)}
                          {!u.is_active && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {u.email}
                          </span>
                          {u.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {u.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        onClick={() => setCredentialsModal({ open: true, user: u })}
                        variant="outline"
                        size="sm"
                        data-testid={`edit-user-btn-${u.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t.edit}
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noUsers}</p>
                </div>
              )}
            </div>
          )}

          {/* Students Tab Content */}
          {activeTab === 'students' && (
            <div>
              {/* CSV Action Buttons */}
              <div className="p-4 bg-gray-50 border-b flex flex-wrap items-center gap-3">
                <Button 
                  onClick={handleExportCSV} 
                  disabled={exporting}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="export-csv-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {t.exportCSV}
                </Button>
                
                <Button 
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="download-template-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  {t.downloadTemplate}
                </Button>
                
                <div className="relative">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    ref={fileInputRef}
                    className="hidden"
                    id="csv-import-input"
                    data-testid="csv-import-input"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                    data-testid="import-csv-btn"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {t.importCSV}
                  </Button>
                </div>

                {/* Send Welcome Emails Checkbox */}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={sendWelcomeEmails}
                    onChange={(e) => setSendWelcomeEmails(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    data-testid="send-emails-checkbox"
                  />
                  <Mail className="w-4 h-4 text-gray-500" />
                  {t.sendWelcomeEmails}
                </label>

                {/* Import Result Message */}
                {importResult && (
                  <div className={`ml-auto px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${
                    importResult.imported > 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    <CheckCircle className="w-4 h-4" />
                    {t.importSuccess} {importResult.imported} {t.imported}, {importResult.skipped} {t.skipped}
                    {importResult.emails_sent > 0 && `, ${importResult.emails_sent} ${t.emailsSent}`}
                  </div>
                )}
              </div>

              {/* Students List */}
              <div className="divide-y">
                {allStudents.filter(s => 
                  s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  s.email?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length > 0 ? (
                  allStudents.filter(s => 
                    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((student) => (
                    <div key={student.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900">{student.name}</h3>
                            {getRoleBadge(student.role)}
                            {!student.is_active && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {student.email}
                            </span>
                            {student.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {student.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button 
                          onClick={() => setCredentialsModal({ open: true, user: student })}
                          variant="outline"
                          size="sm"
                          data-testid={`edit-student-btn-${student.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {t.edit}
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t.noStudents}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Marquee Photos Tab Content */}
          {activeTab === 'marquee' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {language === 'en' ? 'Scrolling Teacher Photos' : 'صور المعلمين المتحركة'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === 'en' ? 'Manage the photos displayed in the homepage marquee' : 'إدارة الصور المعروضة في الشريط المتحرك على الصفحة الرئيسية'}
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setNewMarqueeTeacher({ name: '', title_en: '', title_ar: '', image: null });
                    setMarqueeModal({ open: true, teacher: null });
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Add Photo' : 'إضافة صورة'}
                </Button>
              </div>

              {/* Marquee Teachers Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {marqueeTeachers.map((teacher) => {
                  // Handle Base64 data URLs, external URLs, or relative paths
                  const getTeacherImageSrc = (img) => {
                    if (!img) return 'https://storage.googleapis.com/msgsndr/1yH5m8o1wurAPKrI3BJm/media/67e1e37f1870f446643a1fcf.png';
                    if (img.startsWith('data:')) return img;
                    if (img.startsWith('http')) return img;
                    return `${process.env.REACT_APP_BACKEND_URL}${img}`;
                  };
                  return (
                  <div key={teacher.id} className="relative group">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md">
                      <img
                        src={getTeacherImageSrc(teacher.image)}
                        alt={teacher.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <p className="font-semibold text-sm">{teacher.name}</p>
                        <p className="text-xs text-gray-300">{language === 'en' ? teacher.title_en : teacher.title_ar}</p>
                      </div>
                    </div>
                    {/* Action buttons on hover */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setNewMarqueeTeacher({
                            name: teacher.name,
                            title_en: teacher.title_en,
                            title_ar: teacher.title_ar,
                            image: null
                          });
                          setMarqueeModal({ open: true, teacher });
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-8 w-8 p-0"
                        onClick={() => handleDeleteMarqueeTeacher(teacher.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
                })}
                {marqueeTeachers.length === 0 && (
                  <div className="col-span-full p-12 text-center text-gray-500">
                    <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'en' ? 'No marquee photos added yet' : 'لم تتم إضافة صور بعد'}</p>
                    <p className="text-sm">{language === 'en' ? 'Default photos will be shown on the homepage' : 'سيتم عرض الصور الافتراضية على الصفحة الرئيسية'}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Marquee Teacher Modal */}
      <Dialog open={marqueeModal.open} onOpenChange={(open) => !open && setMarqueeModal({ open: false, teacher: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {marqueeModal.teacher 
                ? (language === 'en' ? 'Edit Photo' : 'تعديل الصورة')
                : (language === 'en' ? 'Add New Photo' : 'إضافة صورة جديدة')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{language === 'en' ? 'Name' : 'الاسم'}</Label>
              <Input
                value={newMarqueeTeacher.name}
                onChange={(e) => setNewMarqueeTeacher({ ...newMarqueeTeacher, name: e.target.value })}
                placeholder={language === 'en' ? 'e.g. Ms. Sarah' : 'مثال: أ. سارة'}
              />
            </div>
            <div>
              <Label>{language === 'en' ? 'Title (English)' : 'اللقب (إنجليزي)'}</Label>
              <Input
                value={newMarqueeTeacher.title_en}
                onChange={(e) => setNewMarqueeTeacher({ ...newMarqueeTeacher, title_en: e.target.value })}
                placeholder="e.g. English Teacher"
              />
            </div>
            <div>
              <Label>{language === 'en' ? 'Title (Arabic)' : 'اللقب (عربي)'}</Label>
              <Input
                value={newMarqueeTeacher.title_ar}
                onChange={(e) => setNewMarqueeTeacher({ ...newMarqueeTeacher, title_ar: e.target.value })}
                placeholder="مثال: معلمة إنجليزي"
                className="text-right"
                dir="rtl"
              />
            </div>
            <div>
              <Label>{language === 'en' ? 'Photo' : 'الصورة'}</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setNewMarqueeTeacher({ ...newMarqueeTeacher, image: e.target.files?.[0] || null })}
                ref={marqueeFileInputRef}
                className="hidden"
              />
              <div className="flex items-center gap-3 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => marqueeFileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Choose Image' : 'اختر صورة'}
                </Button>
                {newMarqueeTeacher.image && (
                  <span className="text-sm text-green-600">✓ {newMarqueeTeacher.image.name}</span>
                )}
                {!newMarqueeTeacher.image && marqueeModal.teacher && (
                  <span className="text-sm text-gray-500">{language === 'en' ? 'Current image will be kept' : 'سيتم الاحتفاظ بالصورة الحالية'}</span>
                )}
              </div>
              <p className="text-xs text-green-600 mt-2">
                {language === 'en' 
                  ? '✓ Images are stored in the database and persist after deployment' 
                  : '✓ الصور مخزنة في قاعدة البيانات وتبقى بعد النشر'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarqueeModal({ open: false, teacher: null })}>
              {language === 'en' ? 'Cancel' : 'إلغاء'}
            </Button>
            <Button 
              onClick={handleSaveMarqueeTeacher}
              disabled={savingMarquee}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingMarquee ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'en' ? 'Save' : 'حفظ')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Modal */}
      <Dialog open={actionModal.open} onOpenChange={(open) => !open && setActionModal({ open: false, teacher: null, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionModal.action === 'approve' ? t.confirmApprove : t.confirmReject}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {successMessage && (
              <div className="bg-green-50 text-green-700 p-3 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {successMessage}
              </div>
            )}
            
            <div>
              <Label>Teacher</Label>
              <p className="text-sm font-medium text-gray-900">{actionModal.teacher?.name}</p>
              <p className="text-sm text-gray-500">{actionModal.teacher?.email}</p>
            </div>
            
            {actionModal.action === 'approve' ? (
              <p className="text-sm text-gray-600">{t.approveMessage}</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">{t.rejectMessage}</p>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t.rejectReason}
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal({ open: false, teacher: null, action: null })}>
              {t.cancel}
            </Button>
            <Button 
              onClick={actionModal.action === 'approve' ? handleApprove : handleReject} 
              disabled={submitting}
              className={actionModal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Credentials Modal */}
      <UserCredentialsModal
        user={credentialsModal.user}
        open={credentialsModal.open}
        onClose={() => setCredentialsModal({ open: false, user: null })}
        onSave={handleSaveCredentials}
      />

      {/* Create Teacher Modal */}
      <Dialog open={createTeacherModal} onOpenChange={setCreateTeacherModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-teal-600" />
              {t.createTeacher}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.teacherName} *</Label>
                <Input
                  value={newTeacher.name}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.teacherEmail} *</Label>
                <Input
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="teacher@example.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.teacherPassword} *</Label>
                <Input
                  type="password"
                  value={newTeacher.password}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.teacherPhone}</Label>
                <Input
                  value={newTeacher.phone}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.teacherBio}</Label>
              <Textarea
                value={newTeacher.bio}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="English teacher bio..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t.teacherBioAr}</Label>
              <Textarea
                value={newTeacher.bio_ar}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, bio_ar: e.target.value }))}
                placeholder="السيرة الذاتية بالعربية..."
                rows={2}
                dir="rtl"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.profileImage}</Label>
                <div className="flex flex-col gap-2">
                  {newTeacher.profile_image && (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img 
                        src={newTeacher.profile_image.startsWith('data:') || newTeacher.profile_image.startsWith('http') ? newTeacher.profile_image : `${process.env.REACT_APP_BACKEND_URL}${newTeacher.profile_image}`} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, false)}
                      className="hidden"
                      id="create-teacher-image"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('create-teacher-image').click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {newTeacher.profile_image ? t.changeImage : t.uploadImage}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Video className="w-4 h-4" />
                  {t.introVideo}
                </Label>
                <Input
                  value={newTeacher.intro_video_url}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, intro_video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.specializations}</Label>
              <Input
                value={newTeacher.specializations}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, specializations: e.target.value }))}
                placeholder="IELTS, Business English, Conversation..."
              />
              <p className="text-xs text-gray-500">
                {language === 'ar' ? 'افصل بين التخصصات بفاصلة' : 'Separate specializations with commas'}
              </p>
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newTeacher.auto_approve}
                onChange={(e) => setNewTeacher(prev => ({ ...prev, auto_approve: e.target.checked }))}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">{t.autoApprove}</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTeacherModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleCreateTeacher}
              disabled={creatingTeacher}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {creatingTeacher ? <Loader2 className="w-4 h-4 animate-spin" /> : t.createTeacher}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Teacher Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, teacher: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {t.deleteConfirmTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{t.deleteConfirmMessage}</p>
            </div>
            <div>
              <Label>Teacher</Label>
              <p className="text-sm font-medium text-gray-900">{deleteModal.teacher?.name}</p>
              <p className="text-sm text-gray-500">{deleteModal.teacher?.email}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, teacher: null })}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleDeleteTeacher}
              disabled={deleting}
              variant="destructive"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.deleteTeacher}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Teacher Modal */}
      <Dialog open={editTeacherModal.open} onOpenChange={(open) => !open && setEditTeacherModal({ open: false, teacher: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-teal-600" />
              {t.editTeacher}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.teacherName}</Label>
                <Input
                  value={editTeacher.name}
                  onChange={(e) => setEditTeacher(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.teacherEmail}</Label>
                <Input
                  type="email"
                  value={editTeacher.email}
                  onChange={(e) => setEditTeacher(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="teacher@example.com"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.teacherPhone}</Label>
              <Input
                value={editTeacher.phone}
                onChange={(e) => setEditTeacher(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1234567890"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t.teacherBio}</Label>
              <Textarea
                value={editTeacher.bio}
                onChange={(e) => setEditTeacher(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="English teacher bio..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t.teacherBioAr}</Label>
              <Textarea
                value={editTeacher.bio_ar}
                onChange={(e) => setEditTeacher(prev => ({ ...prev, bio_ar: e.target.value }))}
                placeholder="السيرة الذاتية بالعربية..."
                rows={2}
                dir="rtl"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.profileImage}</Label>
                <div className="flex flex-col gap-2">
                  {editTeacher.profile_image && (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img 
                        src={editTeacher.profile_image.startsWith('data:') || editTeacher.profile_image.startsWith('http') ? editTeacher.profile_image : `${process.env.REACT_APP_BACKEND_URL}${editTeacher.profile_image}`} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, true)}
                      className="hidden"
                      id="edit-teacher-image"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('edit-teacher-image').click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {editTeacher.profile_image ? t.changeImage : t.uploadImage}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Video className="w-4 h-4" />
                  {t.introVideo}
                </Label>
                <Input
                  value={editTeacher.intro_video_url}
                  onChange={(e) => setEditTeacher(prev => ({ ...prev, intro_video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.specializations}</Label>
              <Input
                value={editTeacher.specializations}
                onChange={(e) => setEditTeacher(prev => ({ ...prev, specializations: e.target.value }))}
                placeholder="IELTS, Business English, Conversation..."
              />
              <p className="text-xs text-gray-500">
                {language === 'ar' ? 'افصل بين التخصصات بفاصلة' : 'Separate specializations with commas'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTeacherModal({ open: false, teacher: null })}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleUpdateTeacher}
              disabled={updatingTeacher}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {updatingTeacher ? <Loader2 className="w-4 h-4 animate-spin" /> : t.updateTeacher}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
